import { formatDistanceToNow } from 'date-fns';
import { useCallback, useContext, useMemo } from 'react';

import { NotificationContext } from '../../../../contexts/notification';
import { INotificationDTO, NotificationType } from '../../../../services';
import { Description, Time, Title } from '../../styles';
import {
  NotificationProcessor,
  processChatMessage,
  processContributionsSet,
  processQuoteDecisionsMade,
  processQuoteUploaded,
  processQuotifyFileFailed,
  processQuotifyFileProcessed,
  processQuotifyFileUploaded,
  processQuotifyPlanStatusChanged,
  processRenewalAwaitingReview,
  processRenewalCompleted,
  processRenewalCreation,
  processRenewalDelete,
  processRenewalUpdate,
  processUnknown,
} from './processor';

interface INotificationItemProps {
  notificationData: INotificationDTO;
}

const renewalUpdateTypes = [
  NotificationType.RENEWAL_DOCUMENT_ADD,
  NotificationType.RENEWAL_DOCUMENT_DELETE,
  NotificationType.RENEWAL_DOCUMENT_APPROVE,
  NotificationType.RENEWAL_DOCUMENT_DECLINE,
  NotificationType.RENEWAL_ADDITIONAL_DOCUMENT_ADD,
  NotificationType.RENEWAL_ADDITIONAL_DOCUMENT_DELETE,
  NotificationType.RENEWAL_ADDITIONAL_DOCUMENT_APPROVE,
  NotificationType.RENEWAL_ADDITIONAL_DOCUMENT_DECLINE,
  NotificationType.RENEWAL_PREMIUM_CALCULATOR_ADD,
  NotificationType.RENEWAL_PREMIUM_CALCULATOR_DELETE,
  NotificationType.RENEWAL_PREMIUM_CALCULATOR_APPROVE,
  NotificationType.RENEWAL_PREMIUM_CALCULATOR_DECLINE,
];

const notificationProcessors: Partial<
  Record<NotificationType, NotificationProcessor>
> = {
  [NotificationType.CHAT_MESSAGE]: processChatMessage,
  [NotificationType.RENEWAL_CREATION]: processRenewalCreation,
  [NotificationType.RENEWAL_DELETE]: processRenewalDelete,
  [NotificationType.QUOTE_UPLOADED]: processQuoteUploaded,
  [NotificationType.QUOTE_DECISIONS_MADE]: processQuoteDecisionsMade,
  [NotificationType.CONTRIBUTIONS_SET]: processContributionsSet,
  [NotificationType.RENEWAL_AWAITING_REVIEW]: processRenewalAwaitingReview,
  [NotificationType.RENEWAL_COMPLETED]: processRenewalCompleted,
  [NotificationType.QUOTIFY_FILE_UPLOADED]: processQuotifyFileUploaded,
  [NotificationType.QUOTIFY_FILE_PROCESSED]: processQuotifyFileProcessed,
  [NotificationType.QUOTIFY_FILE_FAILED]: processQuotifyFileFailed,
  [NotificationType.QUOTIFY_PLAN_STATUS_CHANGED]: processQuotifyPlanStatusChanged,
};

export const NotificationItem = ({
  notificationData,
}: INotificationItemProps): JSX.Element => {
  const context = useContext(NotificationContext);
  const recoverNotificationTitle = useCallback(
    (notificationType: NotificationType) =>
      context.recoverNotificationTitle(notificationType),
    [context]
  );

  const processor = useCallback(
    (data: INotificationDTO) => {
      if (
        Object.prototype.hasOwnProperty.call(
          notificationProcessors,
          data.type
        ) &&
        typeof notificationProcessors[data.type] === 'function'
      ) {
        return (notificationProcessors[data.type] as NotificationProcessor)(
          data,
          recoverNotificationTitle
        );
      } else if (renewalUpdateTypes.includes(data.type)) {
        return processRenewalUpdate(data, recoverNotificationTitle);
      } else {
        return processUnknown(data);
      }
    },
    [recoverNotificationTitle]
  );

  const { title, clientName, description, createdAt } = useMemo(
    () => processor(notificationData),
    [notificationData, processor]
  );

  return (
    <>
      <Title data-cy="notification-title">{title}</Title>
      {clientName !== null && (
        <Title data-cy="notification-client-name">{clientName}</Title>
      )}
      <Description data-cy="notification-description">
        {description}
        {createdAt && <Time>{formatDistanceToNow(new Date(createdAt))}</Time>}
      </Description>
    </>
  );
};
